import React, { useState, useEffect, useCallback } from 'react';
import { Globe, Plus, Trash2, RefreshCw, Shield, CheckCircle, Clock, AlertCircle, ExternalLink, Loader2, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function DnsRecordRow({ record }) {
  const [copied, setCopied] = useState(false);
  const copyValue = () => {
    navigator.clipboard.writeText(record.value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
      <span className="px-2 py-0.5 text-xs font-mono bg-blue-500/20 text-blue-400 rounded">{record.type}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white font-mono truncate">{record.name}</p>
        <p className="text-xs text-gray-400 truncate">{record.value}</p>
      </div>
      <button onClick={copyValue} className="p-1.5 text-gray-400 hover:bg-slate-700 rounded" title="Copiar valor">
        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

function DomainCard({ domain, onVerify, onRequestSSL, onDelete, verifying }) {
  const statusColors = {
    pending_verification: 'text-yellow-400 bg-yellow-500/20',
    verified: 'text-blue-400 bg-blue-500/20',
    active: 'text-green-400 bg-green-500/20'
  };
  const statusLabels = {
    pending_verification: 'Pendiente',
    verified: 'Verificado',
    active: 'Activo'
  };

  return (
    <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-purple-400" />
          <span className="font-medium text-white">{domain.full_domain}</span>
          {domain.ssl_enabled && <Shield className="w-4 h-4 text-green-400" title="SSL activo" />}
        </div>
        <span className={`px-2 py-0.5 text-xs rounded ${statusColors[domain.status] || 'text-gray-400 bg-gray-500/20'}`}>
          {statusLabels[domain.status] || domain.status}
        </span>
      </div>

      {domain.status === 'pending_verification' && (
        <div className="mb-3">
          <p className="text-xs text-gray-400 mb-2">Configura estos registros DNS:</p>
          <div className="space-y-2">
            {domain.dns_records?.map((rec, i) => <DnsRecordRow key={i} record={rec} />)}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {domain.status === 'pending_verification' && (
            <button onClick={() => onVerify(domain.id)} disabled={verifying} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 disabled:opacity-50">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Verificar DNS
            </button>
          )}
          {domain.status === 'verified' && !domain.ssl_enabled && (
            <button onClick={() => onRequestSSL(domain.id)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30">
              <Shield className="w-4 h-4" />
              Activar SSL
            </button>
          )}
          {domain.status === 'active' && (
            <a href={`https://${domain.full_domain}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-400 hover:bg-purple-500/20 rounded-lg">
              <ExternalLink className="w-4 h-4" />
              Visitar
            </a>
          )}
        </div>
        <button onClick={() => onDelete(domain.id)} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {domain.ssl_certificate && (
        <div className="mt-3 pt-3 border-t border-slate-700 text-xs text-gray-400">
          <div className="flex items-center gap-2">
            <Shield className="w-3 h-3 text-green-400" />
            <span>SSL: {domain.ssl_certificate.provider}</span>
            <span>•</span>
            <span>Expira: {new Date(domain.ssl_expiry).toLocaleDateString('es')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function DomainsPanel({ projectId, onClose }) {
  const [domains, setDomains] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [newDomain, setNewDomain] = useState({ domain: '', subdomain: '', dns_provider: 'cloudflare' });

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const token = localStorage.getItem('cloudtexa_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [domainsRes, providersRes] = await Promise.all([
        axios.get(`${API}/api/domains/${projectId}`, { headers }),
        axios.get(`${API}/api/domains/providers`)
      ]);
      setDomains(domainsRes.data.domains || []);
      setProviders(providersRes.data.providers || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addDomain = async () => {
    if (!newDomain.domain.trim()) return;
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.post(`${API}/api/domains/${projectId}`, newDomain, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Dominio añadido');
      setShowAdd(false);
      setNewDomain({ domain: '', subdomain: '', dns_provider: 'cloudflare' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const verifyDomain = async (domainId) => {
    setVerifying(true);
    try {
      const token = localStorage.getItem('cloudtexa_token');
      const res = await axios.post(`${API}/api/domains/${projectId}/${domainId}/verify`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.verified) {
        toast.success('¡Dominio verificado!');
        fetchData();
      } else {
        toast.error('Verificación fallida. Revisa los registros DNS.');
      }
    } catch (e) { toast.error('Error verificando'); }
    setVerifying(false);
  };

  const requestSSL = async (domainId) => {
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.post(`${API}/api/domains/${projectId}/${domainId}/ssl`, { domain: '', email: 'ssl@cloudtexa.com' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('¡SSL activado!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteDomain = async (domainId) => {
    if (!window.confirm('¿Eliminar este dominio?')) return;
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.delete(`${API}/api/domains/${projectId}/${domainId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Dominio eliminado');
      fetchData();
    } catch (e) { toast.error('Error'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-purple-400" /></div>;

  return (
    <div className="h-full flex flex-col bg-slate-900" data-testid="domains-panel">
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-purple-400" />
            <h2 className="font-medium text-white">Dominios</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Info Banner */}
        <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 mb-4 flex items-start gap-3">
          <Globe className="w-5 h-5 text-purple-400 mt-0.5" />
          <div>
            <p className="text-sm text-purple-300">Conecta tu propio dominio</p>
            <p className="text-xs text-purple-200/70 mt-1">Configura dominios personalizados con SSL gratuito para tu aplicación.</p>
          </div>
        </div>

        {/* Add Domain Button */}
        <button onClick={() => setShowAdd(true)} className="w-full mb-4 py-3 rounded-xl border-2 border-dashed border-slate-700 text-gray-400 hover:border-purple-500 hover:text-purple-400 flex items-center justify-center gap-2 transition-colors">
          <Plus className="w-5 h-5" />
          Añadir Dominio
        </button>

        {/* Domains List */}
        {domains.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Globe className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No tienes dominios configurados</p>
            <p className="text-xs mt-1">Añade un dominio para empezar</p>
          </div>
        ) : (
          <div className="space-y-4">
            {domains.map(d => (
              <DomainCard key={d.id} domain={d} onVerify={verifyDomain} onRequestSSL={requestSSL} onDelete={deleteDomain} verifying={verifying} />
            ))}
          </div>
        )}

        {/* How it works */}
        <div className="mt-6 p-4 rounded-xl bg-slate-800/50 border border-slate-700">
          <h3 className="text-sm font-medium text-white mb-3">¿Cómo funciona?</h3>
          <div className="space-y-3 text-xs text-gray-400">
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs">1</span>
              <p>Añade tu dominio y selecciona tu proveedor DNS</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs">2</span>
              <p>Configura los registros DNS en tu proveedor</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs">3</span>
              <p>Verifica la configuración y activa SSL</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Domain Modal */}
      {showAdd && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-4 w-full max-w-md mx-4">
            <h3 className="font-medium text-white mb-4">Añadir Dominio</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Dominio</label>
                <input type="text" value={newDomain.domain} onChange={e => setNewDomain({ ...newDomain, domain: e.target.value })} placeholder="ejemplo.com" className="w-full px-3 py-2 rounded-lg bg-slate-700 text-white outline-none" />
              </div>
              
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Subdominio (opcional)</label>
                <input type="text" value={newDomain.subdomain} onChange={e => setNewDomain({ ...newDomain, subdomain: e.target.value })} placeholder="app" className="w-full px-3 py-2 rounded-lg bg-slate-700 text-white outline-none" />
                <p className="text-xs text-gray-500 mt-1">Ej: app.ejemplo.com</p>
              </div>
              
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Proveedor DNS</label>
                <select value={newDomain.dns_provider} onChange={e => setNewDomain({ ...newDomain, dns_provider: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-700 text-white outline-none">
                  {providers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-gray-400 hover:bg-slate-700 rounded-lg">Cancelar</button>
              <button onClick={addDomain} className="px-4 py-2 text-sm bg-purple-500 text-white rounded-lg">Añadir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DomainsPanel;
